/**
 * Affiliate referral / coupon cookie capture (Stage 1 of the affiliate
 * feature). The landing and the app share these cookies, so the domain is
 * the registrable parent (`.seller2go.com`) whenever the app runs on a
 * seller2go.com host; on localhost the domain attribute is omitted (setting
 * a foreign domain would make the browser silently drop the cookie).
 */

const REF_COOKIE_NAME = 's2g_ref';
const COUPON_COOKIE_NAME = 's2g_coupon';
const COOKIE_TTL_DAYS = 90;

const sharedCookieDomain = (): string | null =>
  window.location.hostname.endsWith('seller2go.com') ? '.seller2go.com' : null;

const setCookie = (name: string, value: string): void => {
  const maxAge = COOKIE_TTL_DAYS * 24 * 60 * 60;
  const domain = sharedCookieDomain();
  let cookie = `${name}=${encodeURIComponent(value)}; max-age=${maxAge}; path=/; SameSite=Lax`;
  if (domain) {
    cookie += `; domain=${domain}`;
  }
  document.cookie = cookie;
};

const getCookie = (name: string): string | null => {
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
};

/**
 * Reads `?ref=` / `?coupon=` from the given query string and stores them in
 * the `s2g_ref` / `s2g_coupon` cookies. Last click wins: an existing cookie
 * is always overwritten when a new code arrives.
 */
export const captureReferralParams = (search: string): void => {
  const params = new URLSearchParams(search);
  const ref = params.get('ref');
  if (ref) {
    setCookie(REF_COOKIE_NAME, ref);
  }
  const coupon = params.get('coupon');
  if (coupon) {
    setCookie(COUPON_COOKIE_NAME, coupon);
  }
};

/** Referral code stored from a `?ref=` visit, if any. */
export const getStoredReferralCode = (): string | null => getCookie(REF_COOKIE_NAME);

/**
 * Removes the `s2g_ref` cookie (same path/domain it was set with). Called
 * after a successful registration so the referral stops affecting later
 * visits (e.g. the login-vs-register redirect).
 */
export const clearStoredReferralCode = (): void => {
  const domain = sharedCookieDomain();
  let cookie = `${REF_COOKIE_NAME}=; max-age=0; path=/; SameSite=Lax`;
  if (domain) {
    cookie += `; domain=${domain}`;
  }
  document.cookie = cookie;
};

/** Coupon code stored from a `?coupon=` visit, if any. */
export const getStoredCouponCode = (): string | null => getCookie(COUPON_COOKIE_NAME);
